// Role-based data filtering utilities for NLNG VR LMS

/**
 * Filter VR content based on user role and permissions
 * @param {Array} content - Array of VR content objects from backend API
 * @param {Object} user - Current user object with role, id, etc.
 * @param {Array} modules - Array of module objects from backend API
 * @returns {Array} Filtered content array
 */
export const filterContentByRole = (content, user, modules) => {
  // Backend already filters content by role, return as-is
  return content || [];
};

/**
 * Filter explainer videos based on user role and permissions
 * @param {Array} videos - Array of explainer video objects from backend
 * @param {Object} user - Current user object with role, id, etc.
 * @param {Array} modules - Array of module objects from backend
 * @returns {Array} Filtered videos array
 */
export const filterExplainerVideosByRole = (videos, user, modules) => {
  // Backend already filters videos by role, return as-is
  return videos || [];
};

/**
 * Filter modules based on user role and permissions
 * @param {Array} modules - Array of module objects from backend API
 * @param {Object} user - Current user object with role, id, etc.
 * @returns {Array} Filtered modules array
 */
export const filterModulesByRole = (modules, user) => {
  if (!user || !user.role) return [];

  // Administrator: can see all modules
  if (user.role === 'Administrator') {
    return modules;
  }

  // Instructor: can see modules they created or are assigned to
  if (user.role === 'Instructor') {
    return modules.filter(m =>
      (m.createdBy && m.createdBy === user.id) ||
      (m.instructorIds && m.instructorIds.includes(user.id)) ||
      (user.assignedModules && user.assignedModules.includes(m.id))
    );
  }

  // Trainee: can see modules they are enrolled in
  if (user.role === 'Trainee') {
    return modules.filter(m =>
      user.enrolledModules && user.enrolledModules.includes(m.id)
    );
  }

  // Default: return empty array for unknown roles
  return [];
};

/**
 * Filter users based on current user's role and permissions
 * @param {Array} users - Array of user objects from backend API
 * @param {Object} currentUser - Current user object making the request
 * @returns {Array} Filtered users array
 */
export const filterUsersByRole = (users, currentUser) => {
  if (!currentUser || !currentUser.role) return [];

  // Administrator: can see all users
  if (currentUser.role === 'Administrator') {
    return users;
  }

  // Instructor: can see trainees (for enrollment) and other instructors
  if (currentUser.role === 'Instructor') {
    return users.filter(u =>
      u.role === 'Trainee' || u.role === 'Instructor'
    );
  }

  // Trainee: can see themselves and all instructors, but not other trainees
  if (currentUser.role === 'Trainee') {
    return users.filter(u => u.id === currentUser.id || u.role === 'Instructor');
  }

  // Default: return empty array for unknown roles
  return [];
};

/**
 * Check if a user has access to a specific module
 * @param {Object} user - Current user object
 * @param {Object} module - Module object to check access for
 * @returns {boolean} True if user has access to the module
 */
export const checkUserAccessToModule = (user, module) => {
  if (!user || !user.role || !module) return false;

  if (user.role === 'Administrator') return true;

  if (user.role === 'Instructor') {
    return (
      (module.createdBy && module.createdBy === user.id) ||
      (module.instructorIds && module.instructorIds.includes(user.id)) ||
      (user.assignedModules && user.assignedModules.includes(module.id))
    );
  }

  if (user.role === 'Trainee') {
    return user.enrolledModules && user.enrolledModules.includes(module.id);
  }

  return false;
};

/**
 * Get role-specific analytics data
 * @param {Object} analyticsData - Full analytics data from backend API
 * @param {Object} user - Current user object
 * @param {Array} modules - Array of module objects
 * @param {Array} users - Array of user objects
 * @param {Object|null} personalAnalytics - Personal analytics from backend (trainee only)
 * @returns {Object} Filtered analytics data for the user's role
 */
export const filterAnalyticsForUser = (analyticsData, user, modules, users, personalAnalytics) => {
  if (!user || !user.role || !analyticsData) {
    return { monthlyActivity: [], moduleCompletion: [], contentCategories: [], stats: { totalContent: 0, activeModules: 0, enrolledUsers: 0, avgCompletionRate: 0, totalSessions: 0, totalCompletions: 0 } };
  }

  // Administrator: gets full analytics
  if (user.role === 'Administrator') {
    return analyticsData;
  }

  // Instructor: gets analytics for modules they instruct
  if (user.role === 'Instructor') {
    const instructorModules = filterModulesByRole(modules, user);
    const instructorModuleIds = instructorModules.map(m => m.id);

    // Filter module completion data
    const filteredModuleCompletion = analyticsData.moduleCompletion
      ? analyticsData.moduleCompletion.filter(item => {
          const module = modules.find(m => m.title === item.name);
          return module && instructorModuleIds.includes(module.id);
        })
      : [];

    // Calculate stats for instructor's modules
    const totalTrainees = users.filter(u =>
      u.role === 'Trainee' &&
      u.enrolledModules &&
      u.enrolledModules.some(moduleId => instructorModuleIds.includes(moduleId))
    ).length;

    const totalCompletions = filteredModuleCompletion.reduce((sum, item) => {
      const module = modules.find(m => m.title === item.name);
      if (!module) return sum;
      const completionRate = item.rate || 0;
      const enrolled = item.enrolled || 0;
      return sum + Math.round(enrolled * (completionRate / 100));
    }, 0);

    return {
      monthlyActivity: analyticsData.monthlyActivity || [],
      moduleCompletion: filteredModuleCompletion,
      contentCategories: analyticsData.contentCategories || [],
      stats: {
        totalContent: analyticsData.stats?.totalContent || 0,
        activeModules: instructorModules.filter(m => m.status === 'Active').length,
        enrolledUsers: totalTrainees,
        avgCompletionRate: filteredModuleCompletion.length > 0
          ? Math.round(filteredModuleCompletion.reduce((sum, item) => sum + (item.rate || 0), 0) / filteredModuleCompletion.length)
          : 0,
        totalSessions: analyticsData.stats?.totalSessions || 0,
        totalCompletions: totalCompletions,
      }
    };
  }

  // Trainee: gets personal progress analytics from backend
  if (user.role === 'Trainee') {
    const enrolledModules = filterModulesByRole(modules, user);

    // Use real backend personal analytics if available
    if (personalAnalytics) {
      const { moduleProgress = [], overallProgress = 0, totalModules = 0, completedModules = 0, totalTimeSpentHours } = personalAnalytics;

      return {
        monthlyActivity: analyticsData.monthlyActivity || [],
        moduleCompletion: moduleProgress.map(mp => ({
          name: mp.title,
          rate: mp.progress || 0,
          enrolled: 1
        })),
        contentCategories: analyticsData.contentCategories || [],
        stats: {
          totalContent: personalAnalytics.totalContentEnrolled || 0,
          activeModules: totalModules,
          enrolledUsers: 1,
          avgCompletionRate: overallProgress,
          totalSessions: analyticsData.stats?.totalSessions || 0,
          totalCompletions: analyticsData.stats?.totalCompletions || 0,
          totalTimeSpentHours: totalTimeSpentHours || 0,
        },
        // Expose full personal analytics for detailed trainee views
        personalAnalytics,
      };
    }

    // Fallback to local computation if API call failed
    const completedModuleIds = user.completedModules || [];
    const progress = user.progress || 0;

    return {
      monthlyActivity: analyticsData.monthlyActivity || [],
      moduleCompletion: enrolledModules.map(module => ({
        name: module.title,
        rate: completedModuleIds.includes(module.id) ? 100 : 0,
        enrolled: 1
      })),
      contentCategories: analyticsData.contentCategories || [],
      stats: {
        totalContent: 0,
        activeModules: enrolledModules.filter(m => m.status === 'Active').length,
        enrolledUsers: 1,
        avgCompletionRate: progress,
        totalSessions: analyticsData.stats?.totalSessions || 0,
        totalCompletions: analyticsData.stats?.totalCompletions || 0,
      }
    };
  }

  // Default: return empty analytics
  return { monthlyActivity: [], moduleCompletion: [], contentCategories: [], stats: { totalContent: 0, activeModules: 0, enrolledUsers: 0, avgCompletionRate: 0, totalSessions: 0, totalCompletions: 0 } };
};